/**
 * @file PatientEventsView.jsx
 * @author Daniel Aguirre
 * @description Patient Records View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { groupBy } from 'lodash';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';
import ButtonFilled from 'components/ButtonFilled';
import {
  MoreOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import ListItem from 'components/ListItem';
import ModalOptions from 'components/ModalOptions';
import ConfirmModal from 'components/ConfirmModal';

// Helpers
import { sortByDate } from '@nephro/commons/helpers/utils';
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { formatModalDate, getEventIcon } from './helper';

// Styles
import styles from './styles.module.css';

function PatientEventsView({ patientActions, patientEventActions }) {
  const history = useHistory();
  const { t: translate } = useTranslation();
  const [patientEventsDates, setPatientEventsDates] = useState([]);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [patientEvents, setPatientEvents] = useState([]);
  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { getEventTypesByPatientId } = patientActions;
  const { deletePatientEventById } = patientEventActions;

  const formatDate = (date) => moment(date).format('YYYY-MM-DD');
  /*   const formatTime = (date) => moment(date).format('HH:mm:ss'); */

  const setData = (_, response) => {
    if (response?.data) {
      let list = response?.data;

      list = sortByDate(list, 'register_date');

      let newPatientEventList = list.map((itm) => {
        const date = itm.register_date || new Date();
        return {
          date: formatDate(date),
          ...itm
        };
      });
      newPatientEventList = groupBy(newPatientEventList, 'date');
      const datesKeys = Object.keys(newPatientEventList);

      setPatientEvents(newPatientEventList);
      setPatientEventsDates(datesKeys);
    }
  };

  const getEvents = async () => {
    const patientInfo = await getStorageItem('patientInfo');
    if (patientInfo?.id_patient) {
      const data = { patientId: patientInfo.id_patient };
      getEventTypesByPatientId(data, setData);
    }
  };

  useEffect(() => {
    getEvents();
  }, []);

  const validateDateText = (date) => {
    const today = formatDate(moment());
    const yesterday = formatDate(moment().subtract(1, 'days'));
    switch (date) {
      case today:
        return translate('calendarMg.today'); // Today
      case yesterday:
        return translate('patientRecords.yesterday'); // Yesterday
      default:
        return moment(date).format('D MMMM, YYYY'); // Past days
    }
  };

  const onSubmitConfirm = () => {
    const data = { id: selectedItem.id_patient_event };
    deletePatientEventById(data, getEvents);
    setVisibleConfirm(false);
  };

  const onOpenDelete = () => {
    setVisibleDrawer(false);
    setVisibleConfirm(true);
  };

  const modalOptions = [
    {
      icon: <EditOutlined />,
      textOption: 'patientRecords.edit',
      type: 'primary',
      onClick: () =>
        history.push(`/main/events/edit/${selectedItem.id_patient_event}`)
    },
    {
      icon: <DeleteOutlined style={{ color: 'var(--color-error)' }} />,
      textOption: 'patientRecords.delete',
      type: 'error',
      onClick: () => onOpenDelete()
    }
  ];

  const openMore = (itm) => {
    setVisibleDrawer(true);
    setSelectedItem(itm);
  };

  return (
    <AppCardLayout>
      <>
        <div className="px-5 text-baxterGray pt-5">
          <ButtonFilled
            key="1"
            type="cyan"
            onActionClick={() => history.push('/main/events/eventTypes')}
            classStyles={[styles.actionButtonCreate].join(' ')}
            textButton="patientEvents.create"
            iconComponent={<PlusCircleOutlined className={styles.buttonIcon} />}
          />
          <section>
            <p className="my-8" style={{ fontSize: 18 }}>
              {translate('patientEvents.textContent')}
            </p>
            {patientEventsDates.map((itmDate, index1) => {
              const listItems =
                patientEvents[itmDate] !== undefined
                  ? sortByDate(patientEvents[itmDate], 'register_date', 'asc')
                  : [];
              return (
                <div key={index1}>
                  <h2 className="mb-3 mt-7 text-lg font-bold pl-1 md:pl-5">
                    {validateDateText(itmDate)}
                  </h2>
                  <div className="w-full flex flex-col flex-nowrap overflow-x-scroll pb-5 pl-1 md:pl-5">
                    {listItems.map((itm, index2) => {
                      const date = itm.register_date || new Date();
                      return (
                        <div className="min-w-200 w-full max-w-lg" key={index2}>
                          <ListItem
                            icon={getEventIcon(itm.event_type?.id_event_type)}
                            body={
                              <>
                                <h3 className={`mb-0 text-sm font-bold`}>
                                  {translate(
                                    `patientEvents.${itm.event_type?.title}`
                                  )}
                                </h3>
                                <p className="m-0 p-0">{itm.urgency_level}</p>
                                <h3 className="mb-0 text-sm text-baxterGray">
                                  {'Fecha del evento: '}
                                  {moment(date).format('DD/MM/YYYY')}
                                </h3>
                                <h3 className="mb-0 text-sm text-baxterGray">
                                  {'Fecha de registro: '}
                                  {moment(itm.created_at).format('DD/MM/YYYY')}
                                </h3>
                              </>
                            }
                            options={
                              <MoreOutlined
                                onClick={() => openMore(itm)}
                                style={{
                                  fontSize: 24,
                                  color: 'var(--color-baxter-gray)'
                                }}
                              />
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        </div>
        <ConfirmModal
          visibleConfirm={visibleConfirm}
          onCancel={() => setVisibleConfirm(false)}
          mode={'delete'}
          onSubmit={onSubmitConfirm}
          onSubmitText={'patientRecords.delete'}
          contentComponent={
            <div className="bg-highLight flex flex-col justify-center items-center p-2 rounded-md mt-2">
              <h3 className="text-base text-secondary mb-0">
                {selectedItem?.event_type?.title}
              </h3>
              <h3 className="text-base text-secondary mb-0 mt-2">
                <ul>
                  <li>
                    {`${translate('patientEvents.urgencyLevel')}:
                  ${selectedItem?.urgency_level}`}
                  </li>
                  <li>
                    {`${translate('patientEvents.date')}:
                  ${formatModalDate(selectedItem?.register_date, translate)}`}
                  </li>
                  <li>
                    {`${translate('patientEvents.professional')}:
                  ${selectedItem?.attendee}`}
                  </li>
                </ul>
              </h3>
            </div>
          }
        />
        <ModalOptions
          modalOptions={modalOptions}
          visible={visibleDrawer}
          setVisible={setVisibleDrawer}
        />
      </>
    </AppCardLayout>
  );
}

/**
 * Component definition of proptypes.
 */
PatientEventsView.propTypes = {
  patientActions: PropTypes.shape({
    getEventTypesByPatientId: PropTypes.func.isRequired
  }).isRequired,
  patientEventActions: PropTypes.shape({
    deletePatientEventById: PropTypes.func.isRequired
  }).isRequired
};

export default PatientEventsView;
